import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { SEO } from '@/components/SEO';
import { useAuth } from '@/hooks/useAuth';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { INVOICE_TEMPLATES, type TemplateId, type InvoiceTemplate } from '@/components/invoice/InvoiceTemplates';
import { InvoicePrintView } from '@/components/invoice/InvoicePrintView';
import { ArrowRight, Eye, Sparkles, FileText, Download, ShieldCheck, Zap } from 'lucide-react';

const categoryFilters = [
  { id: 'all', label: 'All Templates' },
  { id: 'corporate', label: 'Corporate & B2B' },
  { id: 'minimalist', label: 'Modern & Minimalist' },
  { id: 'creative', label: 'Creative & Freelance' },
];

const templateCategories: Record<string, TemplateId[]> = {
  all: ['none', 'clean', 'ocean', 'midnight', 'forest', 'rose', 'sunset', 'slate', 'golden', 'aurora', 'carbon'],
  corporate: ['ocean', 'midnight', 'slate', 'carbon'],
  minimalist: ['none', 'clean', 'forest', 'slate'],
  creative: ['sunset', 'rose', 'golden', 'aurora'],
};

const templateTags: Record<TemplateId, string[]> = {
  none: ['Freelancers', 'Consultants', 'Simple Billing'],
  clean: ['Clean & Simple', 'Light Accent', 'Modern'],
  ocean: ['B2B Contracts', 'Corporate', 'Enterprise'],
  midnight: ['Executives', 'Agencies', 'High Tech'],
  forest: ['Architects', 'Designers', 'Two-Column'],
  rose: ['Legal', 'Boutiques', 'Serif Classic'],
  sunset: ['Creative Arts', 'Photographers', 'Writers'],
  slate: ['Engineers', 'IT Consultants', 'Clean Slate'],
  golden: ['Luxury Services', 'Consulting', 'Warm Gold'],
  aurora: ['Web Agencies', 'Startups', 'Vibrant'],
  carbon: ['Contractors', 'Trade Services', 'Construction'],
};

const structuredData = {
  '@context': 'https://schema.org',
  '@type': 'CollectionPage',
  name: 'Free Invoice Templates — Quick Free Invoice Tool',
  description:
    'Browse 9+ free professional invoice templates. Select a template and customize your PDF & Word invoice instantly in our free invoice generator.',
  url: 'https://quickfreeinvoicetool.com/templates',
  mainEntity: {
    '@type': 'ItemList',
    itemListElement: INVOICE_TEMPLATES.map((tpl, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: tpl.name,
      description: tpl.description,
      url: `https://quickfreeinvoicetool.com/try-it-out?template=${tpl.id}`,
    })),
  },
};

export default function Templates() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [activeCategory, setActiveCategory] = useState('all');
  const [previewTemplate, setPreviewTemplate] = useState<InvoiceTemplate | null>(null);

  const filteredTemplates = INVOICE_TEMPLATES.filter((tpl) =>
    templateCategories[activeCategory]?.includes(tpl.id)
  );

  const handleSelectTemplate = (id: TemplateId) => {
    if (user) {
      navigate(`/invoices/new?template=${id}`);
    } else {
      navigate(`/try-it-out?template=${id}`);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50/50">
      <SEO
        title="Free Invoice Templates — Browse & Customize Professional Invoice Designs | QuickFreeInvoice"
        description="Browse 9+ free professional invoice templates (Minimalist, Corporate Accent, Modern Executive, Left Sidebar, Elegant Classic, Sunset Glow, Slate, Aurora, Carbon). Preview templates and customize PDF & Word invoices instantly."
        keywords="free invoice templates, professional invoice templates, pdf invoice templates, word invoice template, online invoice maker templates, corporate invoice template, freelance invoice template, modern invoice design, free estimate templates"
        canonical="https://quickfreeinvoicetool.com/templates"
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }}
      />

      {/* Hero */}
      <section className="bg-gradient-to-br from-indigo-900 via-slate-900 to-purple-950 text-white py-20">
        <div className="container mx-auto px-4 text-center max-w-4xl">
          <Badge className="bg-indigo-500/30 text-indigo-200 border-indigo-400/30 mb-4 px-3 py-1 text-xs font-semibold uppercase tracking-wider rounded-full">
            <Sparkles className="h-3.5 w-3.5 mr-1.5 inline-block text-indigo-300" />
            Free Template Gallery
          </Badge>
          <h1 className="font-heading text-4xl md:text-5xl font-extrabold mb-6 leading-tight">
            Free Professional Invoice Templates — Pick a Style &amp; Bill Instantly
          </h1>
          <p className="text-slate-300 text-lg max-w-2xl mx-auto leading-relaxed mb-8">
            Select from clean, corporate, modern, and classic invoice designs. Customize your colors, upload your logo, and export pixel-perfect PDF or Word documents in 60 seconds.
          </p>

          {/* Filter Pills */}
          <div className="flex flex-wrap justify-center gap-2 max-w-2xl mx-auto">
            {categoryFilters.map((cat) => (
              <button
                key={cat.id}
                onClick={() => setActiveCategory(cat.id)}
                className={`px-4 py-2 text-xs md:text-sm font-semibold rounded-full transition-all ${
                  activeCategory === cat.id
                    ? 'bg-indigo-500 text-white shadow-md shadow-indigo-500/20'
                    : 'bg-white/10 text-slate-300 hover:bg-white/20 hover:text-white'
                }`}
              >
                {cat.label}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Templates Grid */}
      <section className="py-16">
        <div className="container mx-auto px-4 max-w-6xl">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredTemplates.map((tpl) => {
              const tags = templateTags[tpl.id] || ['General Business'];
              return (
                <Card
                  key={tpl.id}
                  className="group border-slate-200 hover:border-indigo-400 hover:shadow-xl transition-all duration-300 overflow-hidden flex flex-col bg-white"
                >
                  {/* Live Visual Preview Mockup */}
                  <div className="w-full h-56 p-4 bg-slate-100/70 border-b border-slate-200/80 relative overflow-hidden select-none flex flex-col justify-between">
                    {/* Visual Invoice Paper Container */}
                    <div
                      className="w-full h-full bg-white rounded-lg shadow-sm border border-slate-200/90 p-3.5 flex flex-col justify-between transition-transform group-hover:scale-[1.02] duration-300"
                      style={tpl.backgroundStyle}
                    >
                      {/* Top Bar Header */}
                      <div
                        className="w-full rounded p-2 flex justify-between items-center mb-2"
                        style={{
                          backgroundColor: tpl.tableHeaderBg,
                          color: tpl.tableHeaderColor,
                        }}
                      >
                        <div className="font-heading font-extrabold text-[11px] tracking-wider uppercase">
                          INVOICE
                        </div>
                        <div className="text-[10px] font-semibold opacity-90">#INV-2026-001</div>
                      </div>

                      {/* Address & Meta */}
                      <div className="flex justify-between items-start text-[9px] mb-2 px-1">
                        <div>
                          <div className="font-bold text-slate-800" style={{ color: tpl.headingColor }}>
                            Acme Services Ltd
                          </div>
                          <div className="text-slate-500 text-[8px]" style={{ color: tpl.labelColor }}>
                            1209 Orange Street, DE
                          </div>
                        </div>
                        <div className="text-right text-slate-500 text-[8px]">
                          <div>Date: Oct 24, 2026</div>
                          <div className="font-semibold text-slate-700">Due: Nov 07, 2026</div>
                        </div>
                      </div>

                      {/* Line Items Table Simulation */}
                      <div className="w-full text-[8px] my-1">
                        <div
                          className="flex justify-between font-bold py-1 px-1.5 rounded-t text-[8px]"
                          style={{ backgroundColor: tpl.tableHeaderBg, color: tpl.tableHeaderColor }}
                        >
                          <span>Description</span>
                          <span>Amount</span>
                        </div>
                        <div className="flex justify-between py-1 px-1.5 border-b text-slate-600" style={{ borderColor: tpl.rowBorderColor }}>
                          <span>Web Design &amp; Development</span>
                          <span>$2,500.00</span>
                        </div>
                        <div className="flex justify-between py-1 px-1.5 border-b text-slate-600" style={{ borderColor: tpl.rowBorderColor }}>
                          <span>SEO Optimization</span>
                          <span>$850.00</span>
                        </div>
                      </div>

                      {/* Total Footer Box */}
                      <div
                        className="flex justify-between items-center p-1.5 rounded text-[9px] font-bold mt-auto"
                        style={{ backgroundColor: tpl.totalBg, color: tpl.headingColor }}
                      >
                        <span style={{ color: tpl.labelColor }}>Total Due:</span>
                        <span className="text-[10px]">$3,350.00</span>
                      </div>
                    </div>

                    {/* Quick Preview Badge Hover Overlay */}
                    <button
                      onClick={() => setPreviewTemplate(tpl)}
                      className="absolute inset-0 bg-slate-900/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2 text-white text-xs font-bold backdrop-blur-[2px]"
                      aria-label={`Preview ${tpl.name} template`}
                    >
                      <Eye className="h-4 w-4" />
                      Quick Full Preview
                    </button>
                  </div>

                  {/* Template Info & Action */}
                  <CardContent className="p-5 flex-1 flex flex-col justify-between">
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <h2 className="font-heading text-lg font-bold text-slate-900 group-hover:text-indigo-600 transition-colors">
                          {tpl.name}
                        </h2>
                        <span
                          className="w-3.5 h-3.5 rounded-full border shadow-sm shrink-0"
                          style={{ backgroundColor: tpl.tableHeaderColor }}
                          title={`Primary accent: ${tpl.tableHeaderColor}`}
                        />
                      </div>
                      <p className="text-xs text-slate-500 mb-4 leading-relaxed line-clamp-2">
                        {tpl.description}
                      </p>

                      {/* Tags */}
                      <div className="flex flex-wrap gap-1.5 mb-5">
                        {tags.map((tag) => (
                          <span
                            key={tag}
                            className="bg-slate-100 text-slate-600 text-[10px] font-medium px-2 py-0.5 rounded-full"
                          >
                            {tag}
                          </span>
                        ))}
                      </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex gap-2 pt-2 border-t border-slate-100">
                      <Button
                        onClick={() => handleSelectTemplate(tpl.id)}
                        className="flex-1 bg-indigo-600 hover:bg-indigo-700 text-white font-bold text-xs h-9 shadow-sm"
                      >
                        Use This Template
                        <ArrowRight className="ml-1.5 h-3.5 w-3.5" />
                      </Button>
                      <Button
                        variant="outline"
                        onClick={() => setPreviewTemplate(tpl)}
                        className="text-xs h-9 px-3 border-slate-200 text-slate-600 hover:bg-slate-50"
                        title="Preview layout in detail"
                      >
                        <Eye className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      <section className="border-t border-slate-200 bg-white py-16">
        <div className="container mx-auto max-w-6xl px-4">
          <div className="mx-auto mb-9 max-w-3xl text-center">
            <h2 className="font-heading text-3xl font-bold text-slate-900">Invoice templates by profession</h2>
            <p className="mt-3 text-slate-600">Use practical global invoicing guidance for the way you work, then customize a free template in minutes.</p>
          </div>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {[
              ['freelancer', 'Freelancer', 'Hourly work, projects, and milestones'],
              ['contractor', 'Contractor', 'Labor, materials, deposits, and progress payments'],
              ['consultant', 'Consultant', 'Advisory work, retainers, and workshops'],
              ['agency', 'Agency', 'Campaigns, production, and recurring services'],
            ].map(([slug, title, description]) => (
              <Link key={slug} to={`/invoice-template/${slug}`} className="rounded-xl border border-slate-200 bg-slate-50 p-5 transition hover:border-indigo-300 hover:bg-indigo-50">
                <h3 className="font-heading font-bold text-slate-900">{title} invoice template</h3>
                <p className="mt-2 text-sm leading-relaxed text-slate-600">{description}</p>
                <span className="mt-4 inline-flex items-center text-sm font-semibold text-indigo-600">View guide <ArrowRight className="ml-1 h-4 w-4" /></span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* SEO Content: How to Choose Invoice Templates */}
      <section className="py-20 bg-white border-t">
        <div className="container mx-auto px-4 max-w-4xl">
          <h2 className="font-heading text-3xl font-bold mb-6 text-slate-900">
            How to Choose the Right Free Invoice Template for Your Business
          </h2>
          <div className="prose prose-slate max-w-none text-slate-600 space-y-4">
            <p>
              A well-formatted invoice template is more than just a billing form — it's a reflection of your brand professionalism. Sending a clean, structured invoice encourages clients to pay promptly and builds trust.
            </p>
            <h3>Key Factors to Consider:</h3>
            <ul className="space-y-2 text-sm">
              <li>
                <strong>Corporate &amp; B2B Billing (Corporate Accent / Modern Executive):</strong> If you invoice corporate clients or enterprise businesses, use structured headers with dark accents like <em>Corporate Accent</em> or <em>Modern Executive</em>. They present clear line items and formal tax calculation breakdowns.
              </li>
              <li>
                <strong>Freelance &amp; Creative Services (Sunset Glow / Aurora Indigo):</strong> For designers, photographers, and copywriters, warm and vibrant templates like <em>Sunset Glow</em> or <em>Aurora Indigo</em> add a touch of personality while keeping layout elements clean and legible.
              </li>
              <li>
                <strong>Trades &amp; Construction (Carbon Steel / Left Sidebar):</strong> High-contrast templates like <em>Carbon Steel</em> and <em>Left Sidebar</em> make it easy to itemize raw materials, equipment rentals, labor hours, and subcontractor fees clearly.
              </li>
              <li>
                <strong>Legal &amp; Professional Services (Elegant Classic):</strong> Serif typography and subtle double borders on <em>Elegant Classic</em> give formal advisory, accounting, and legal billing a traditional, polished feel.
              </li>
            </ul>
          </div>
        </div>
      </section>

      {/* Features Banner */}
      <section className="py-16 bg-slate-900 text-white">
        <div className="container mx-auto px-4 max-w-5xl">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 text-center">
            {[
              { icon: <Zap className="h-6 w-6 text-indigo-400 mx-auto mb-2" />, title: '100% Free', desc: 'No watermark or mandatory sign-up' },
              { icon: <Download className="h-6 w-6 text-emerald-400 mx-auto mb-2" />, title: 'PDF & Word Exports', desc: 'Download in both file formats instantly' },
              { icon: <ShieldCheck className="h-6 w-6 text-purple-400 mx-auto mb-2" />, title: '170+ Currencies', desc: 'Supports global multi-currency billing' },
              { icon: <FileText className="h-6 w-6 text-amber-400 mx-auto mb-2" />, title: 'VAT & GST Tax Ready', desc: 'Auto-calculates global sales taxes' },
            ].map((f) => (
              <div key={f.title} className="p-4 rounded-lg bg-white/5 border border-white/10">
                {f.icon}
                <div className="font-bold text-sm mb-1">{f.title}</div>
                <div className="text-xs text-slate-400">{f.desc}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-gradient-to-r from-indigo-600 to-purple-600 text-white text-center">
        <div className="container mx-auto px-4 max-w-3xl">
          <h2 className="font-heading text-3xl font-bold mb-4">
            Start Creating Your Invoice with Your Favorite Template
          </h2>
          <p className="text-indigo-100 text-lg mb-8">
            Pick any template above or click below to launch the instant invoice generator.
          </p>
          <div className="flex justify-center">
            <Button
              onClick={() => handleSelectTemplate('ocean')}
              size="lg"
              className="bg-white text-indigo-600 hover:bg-indigo-50 font-bold shadow-lg"
            >
              Create Free Invoice Now
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </div>
        </div>
      </section>

      {/* Interactive Full Preview Modal */}
      {previewTemplate && (
        <Dialog open={!!previewTemplate} onOpenChange={() => setPreviewTemplate(null)}>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl flex items-center justify-between">
                <span>{previewTemplate.name} Template Preview</span>
              </DialogTitle>
              <DialogDescription>
                {previewTemplate.description}
              </DialogDescription>
            </DialogHeader>

            {/* High Fidelity Full Page Preview Component */}
            <div className="w-full border rounded-xl overflow-hidden shadow-sm my-4">
              <InvoicePrintView
                invoice={{
                  invoiceNumber: 'INV-2026-0001',
                  issueDate: new Date('2026-10-24'),
                  dueDate: new Date('2026-11-07'),
                  currency: 'USD',
                  subtotal: 4250,
                  totalAmount: 5100,
                  templateId: previewTemplate.id,
                  businessInfo: {
                    name: 'My Business Name',
                    address: '1209 North Orange Street\nWilmington, DE 19801',
                    email: 'billing@mycompany.com',
                    phone: '+1 (302) 555-0199',
                  },
                  clientInfo: {
                    name: 'Acme Corporation LLC',
                    address: '1600 Amphitheatre Parkway\nMountain View, CA 94043',
                    email: 'accounts@client.com',
                    phone: '+1 (650) 253-0000',
                  },
                  items: [
                    { description: 'Professional Web Design & Development Services', quantity: 1, rate: 3500, amount: 3500 },
                    { description: 'Cloud Infrastructure Setup & SEO Optimization', quantity: 1, rate: 750, amount: 750 },
                  ],
                  headerTaxes: [{ type: 'vat', value: 20, label: 'VAT', enabled: true }],
                  notes: 'Thank you for choosing us! Payment is expected within terms.',
                  terms: 'Payment is due upon receipt of invoice.',
                } as any}
                noShadow
              />
            </div>

            {/* Modal Actions */}
            <div className="flex justify-end gap-3 pt-4 border-t border-slate-100">
              <Button variant="outline" onClick={() => setPreviewTemplate(null)}>
                Close Preview
              </Button>
              <Button
                onClick={() => {
                  const id = previewTemplate.id;
                  setPreviewTemplate(null);
                  handleSelectTemplate(id);
                }}
                className="bg-indigo-600 hover:bg-indigo-700 text-white font-bold"
              >
                Use This Template
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
